import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Fuel, Activity, Maximize2 } from "lucide-react";

import { useTranslation } from "react-i18next";
const litres = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "-"
    : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;

function elapsedShort(fromIso) {
  if (!fromIso) return "";
  const t = new Date(fromIso).getTime();
  if (!t) return "";
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

export default function LiveNozzleStatus({ compact = false }) {
  const { t: __t } = useTranslation();
  const [nozzles, setNozzles] = useState([]);
  const [attendance, setAttendance] = useState({ active: [] });
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const [nz, att] = await Promise.all([
        api.get("/nozzles/status"),
        api.get("/attendance/live").catch(() => ({ data: { active: [] } })),
      ]);
      setNozzles(nz.data.nozzles || []);
      setAttendance({ active: att.data.active || [] });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const clockTick = setInterval(() => setTick((x) => x + 1), 60000);
    return () => { clearInterval(t); clearInterval(clockTick); };
  }, []);

  const byMachine = useMemo(() => {
    const m = {};
    for (const n of nozzles) {
      (m[n.machine_code] = m[n.machine_code] || []).push(n);
    }
    return m;
  }, [nozzles]);

  const allocatedCount = nozzles.filter((n) => n.status === "ALLOCATED").length;
  const activeStaff = attendance.active || [];

  // Live litres = closing - opening - testing (or 0 if no closing yet)
  const liveLitres = (a) => {
    if (!a) return null;
    const opening = Number(a.opening) || 0;
    const closing = Number(a.closing);
    const testing = Number(a.testing) || 0;
    if (Number.isNaN(closing) || closing <= 0) return null;
    return Math.max(0, closing - opening - testing);
  };

  return (
    <Card className="border-border" data-testid="live-nozzle-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2 font-display">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Activity className="h-4 w-4" />
            </span>
            Live floor
          </span>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="tabular-nums">
                {allocatedCount} of {nozzles.length} nozzle{nozzles.length === 1 ? "" : "s"} active
              </span>
            </span>
            {!compact && (
              <Link
                to="/manager/live-board"
                data-testid="live-board-link"
                className="hidden items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary sm:inline-flex"
              >
                <Maximize2 className="h-3 w-3" /> Full board
              </Link>
            )}
          </div>
        </CardTitle>
        {activeStaff.length > 0 && (
          <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
            <Users className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="truncate">On floor: {activeStaff.map((a) => `${a.user_name} (${a.shift_name})`).join(", ")}</span>
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.keys(byMachine).sort().map((code) => (
          <div key={code}>
            <div className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>{__t("csa.machine")}</span>
              <span className="font-mono text-foreground">{code}</span>
              <div className="h-px flex-1 bg-border/60" />
            </div>
            <div className={compact ? "grid grid-cols-3 gap-2 md:grid-cols-6" : "grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"}>
              {byMachine[code].map((n) => {
                const active = n.status === "ALLOCATED";
                const lts = active ? liveLitres(n.allocation) : null;
                return (
                  <div
                    key={n.id}
                    className={
                      "relative overflow-hidden rounded-lg border p-3 transition-all " +
                      (active
                        ? "border-primary/60 bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary)/0.15),0_8px_24px_-16px_hsl(var(--primary)/0.35)]"
                        : "border-border bg-card hover:border-primary/30")
                    }
                    data-testid={`live-nozzle-${n.machine_code}-${n.code}`}
                    title={n.allocation ? `${n.allocation.user_name} · ${n.allocation.opening ?? "-"} → ${n.allocation.closing ?? "live"}` : "Free"}
                  >
                    {active && (
                      <span aria-hidden className="pointer-events-none absolute -right-4 -top-4 h-14 w-14 rounded-full bg-primary/10 blur-xl" />
                    )}
                    <div className="relative flex items-center justify-between">
                      <div className="font-mono text-sm font-bold text-foreground">{n.code}</div>
                      <div className={"flex h-2 w-2 rounded-full " + (active ? "bg-primary" : "bg-border")} />
                    </div>
                    <div className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                      {n.product_name}
                    </div>
                    {active && n.allocation && (
                      <div className="relative mt-2 space-y-1">
                        <div className="truncate text-[11px] font-semibold text-primary">
                          {n.allocation.user_name}
                        </div>
                        <div className="flex items-baseline justify-between gap-1">
                          <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                            {lts != null ? litres(lts) : "—"}
                          </span>
                          <span className="text-[10px] tabular-nums text-muted-foreground" data-live-tick={tick}>
                            {elapsedShort(n.allocation.allocated_at)}
                          </span>
                        </div>
                      </div>
                    )}
                    {!active && (
                      <div className="relative mt-2 text-[10px] text-muted-foreground/70">{__t("components.livenozzlestatus.free")}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {nozzles.length === 0 && <p className="text-xs text-muted-foreground">{__t("components.livenozzlestatus.no_nozzles_configured_yet")}</p>}
      </CardContent>
    </Card>
  );
}
